import React, { useEffect, useRef, useState } from 'react';
import { Button, Label, Select, TextInput } from '../../components/ui';
import { CopyIcon, PencilIcon, PlusIcon, TrashIcon } from '../../components/ui/Icons';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import type { EFormFieldSchema } from './schema';
import {
  coerceSubformRows,
  emptySubformRow,
  ensureSubformRowIds,
  formatFieldDisplayValue,
  newSubformRowId,
  subformRowKey,
  EFORM_SUBFORM_ROW_ID_KEY,
} from './schema';

function NestedFieldControl({
  field,
  value,
  onChange,
  onBlur,
  readOnly,
  id,
  compact,
  error,
}: {
  field: EFormFieldSchema;
  value: unknown;
  onChange: (v: unknown) => void;
  onBlur?: () => void;
  readOnly?: boolean;
  id?: string;
  compact?: boolean;
  error?: string;
}) {
  const hasError = Boolean(error);
  if (field.type === 'checkbox' || field.type === 'switch') {
    return (
      <label className={cn('inline-flex items-center gap-2 text-sm', compact && 'gap-1.5')}>
        <input
          id={id}
          type="checkbox"
          className="rounded"
          checked={value === true || value === 'true' || value === 1}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.checked)}
          onBlur={onBlur}
          aria-label={compact ? field.label : undefined}
          aria-invalid={hasError || undefined}
        />
        {!compact ? <span>{field.label}</span> : null}
      </label>
    );
  }

  if (field.type === 'select' || field.type === 'radio') {
    const options = [
      ...(field.required ? [] : [{ value: '', label: '—' }]),
      ...(field.options ?? []).map((o) => ({ value: o.value, label: o.label })),
    ];
    return (
      <Select
        id={id}
        value={value == null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        onClose={onBlur}
        options={options}
        disabled={readOnly}
        size={compact ? 'sm' : 'default'}
        error={compact ? undefined : error}
        triggerClassName={cn(compact && hasError && 'border-red-400')}
      />
    );
  }

  if (field.type === 'textarea') {
    return (
      <textarea
        id={id}
        className={cn(
          'w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900',
          !compact && 'px-3 py-2',
          compact && 'min-w-[8rem]',
          hasError && 'border-red-400',
        )}
        rows={compact ? 2 : 3}
        value={value == null ? '' : String(value)}
        disabled={readOnly}
        placeholder={field.placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={hasError || undefined}
      />
    );
  }

  const inputType =
    field.type === 'number'
      ? 'number'
      : field.type === 'date'
        ? 'date'
        : field.type === 'email'
          ? 'email'
          : field.type === 'time'
            ? 'time'
            : 'text';

  return (
    <TextInput
      id={id}
      type={inputType}
      value={value == null ? '' : String(value)}
      disabled={readOnly}
      placeholder={field.placeholder || (field.type === 'time' ? 'HH:MM' : undefined)}
      error={compact ? undefined : error}
      className={cn(compact && hasError && 'border-red-400')}
      onChange={(e) => {
        if (field.type === 'number') {
          const raw = e.target.value;
          onChange(raw === '' ? '' : Number(raw));
        } else {
          onChange(e.target.value);
        }
      }}
      onBlur={onBlur}
    />
  );
}

function isBlankCell(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/** Per-cell validation for a single subform row draft. */
export function validateRowDraft(
  columns: EFormFieldSchema[],
  draft: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const col of columns) {
    const v = draft[col.key];
    if (col.required && isBlankCell(v) && col.type !== 'checkbox' && col.type !== 'switch') {
      errors[col.key] = 'Required';
      continue;
    }
    if (col.type === 'number' && !isBlankCell(v) && typeof v === 'number' && !Number.isFinite(v)) {
      errors[col.key] = 'Enter a valid number';
    }
    if (col.type === 'email' && !isBlankCell(v)) {
      const s = String(v);
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) {
        errors[col.key] = 'Enter a valid email';
      }
    }
  }
  return errors;
}

function truncateDisplay(text: string, max = 48): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

type RowEditorMode = 'add' | 'edit' | 'view';

function RowCountHint({
  rows,
  field,
  colors,
}: {
  rows: Record<string, unknown>[];
  field: EFormFieldSchema;
  colors: { textMuted: string };
}) {
  return (
    <span className={`text-xs ${colors.textMuted}`}>
      {rows.length}
      {field.maxRows != null ? ` / ${field.maxRows}` : ''} row
      {rows.length === 1 ? '' : 's'}
      {field.minRows ? ` · min ${field.minRows}` : ''}
    </span>
  );
}

/**
 * Sub-form editor (modal or inline).
 *
 * Inline mode: cell validation runs on blur. Parent submit can also call the
 * function registered via `onRegisterValidate` (returns false if any row fails
 * `validateRowDraft`) before proceeding.
 */
export const EFormSubformField: React.FC<{
  field: EFormFieldSchema;
  value: unknown;
  onChange: (value: Record<string, unknown>[]) => void;
  readOnly?: boolean;
  error?: string;
  /**
   * Registers a validate() that runs `validateRowDraft` on every row (inline mode).
   * Call from parent before submit; returns true when all rows pass.
   * Unregisters on unmount / when switching away from inline.
   */
  onRegisterValidate?: (validate: (() => boolean) | null) => void;
}> = ({ field, value, onChange, readOnly = false, error, onRegisterValidate }) => {
  const { colors } = useTheme();
  const columns = field.fields ?? [];
  const rows = coerceSubformRows(value);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const maxRows = field.maxRows ?? 100;
  const minRows = field.minRows ?? 0;
  const canAdd = !readOnly && rows.length < maxRows;
  const canRemove = !readOnly && rows.length > minRows;
  const useModal = field.rowEditor !== 'inline';

  const [editor, setEditor] = useState<{
    mode: RowEditorMode;
    index: number | null;
  } | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
  /** Inline mode: rowKey → columnKey → message */
  const [inlineErrors, setInlineErrors] = useState<Record<string, Record<string, string>>>({});

  // Persist stable `_rowId` for legacy rows that lack one.
  useEffect(() => {
    if (readOnly) return;
    const { rows: next, changed } = ensureSubformRowIds(coerceSubformRows(value));
    if (changed) onChange(next);
  }, [value, readOnly, onChange]);

  useEffect(() => {
    if (!onRegisterValidate) return;
    if (useModal || readOnly) {
      onRegisterValidate(null);
      return;
    }
    onRegisterValidate(() => {
      const current = rowsRef.current;
      const next: Record<string, Record<string, string>> = {};
      let ok = true;
      current.forEach((row, ri) => {
        const key = subformRowKey(row, ri);
        const rowErrs = validateRowDraft(columns, row);
        if (Object.keys(rowErrs).length > 0) {
          next[key] = rowErrs;
          ok = false;
        }
      });
      setInlineErrors(next);
      return ok;
    });
    return () => onRegisterValidate(null);
  }, [onRegisterValidate, useModal, readOnly, columns]);

  const closeEditor = () => {
    setEditor(null);
    setDraftErrors({});
  };

  const openAdd = () => {
    if (!canAdd) return;
    setDraft(emptySubformRow(field));
    setDraftErrors({});
    setEditor({ mode: 'add', index: null });
  };

  const openEdit = (index: number) => {
    setDraft({ ...(rows[index] ?? emptySubformRow(field)) });
    setDraftErrors({});
    setEditor({ mode: readOnly ? 'view' : 'edit', index });
  };

  const openView = (index: number) => {
    setDraft({ ...(rows[index] ?? emptySubformRow(field)) });
    setDraftErrors({});
    setEditor({ mode: 'view', index });
  };

  const saveDraft = () => {
    if (!editor || editor.mode === 'view') return;
    const nextErrors = validateRowDraft(columns, draft);
    setDraftErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (editor.mode === 'add') {
      const row = {
        ...draft,
        [EFORM_SUBFORM_ROW_ID_KEY]:
          typeof draft[EFORM_SUBFORM_ROW_ID_KEY] === 'string'
            ? draft[EFORM_SUBFORM_ROW_ID_KEY]
            : newSubformRowId(),
      };
      onChange([...rows, row]);
    } else if (editor.index != null) {
      onChange(rows.map((r, i) => (i === editor.index ? { ...draft } : { ...r })));
    }
    closeEditor();
  };

  const updateRow = (index: number, key: string, cell: unknown) => {
    const next = rows.map((r, i) => (i === index ? { ...r, [key]: cell } : { ...r }));
    onChange(next);
  };

  const validateInlineRow = (index: number) => {
    const row = rows[index];
    if (!row) return;
    const key = subformRowKey(row, index);
    const rowErrs = validateRowDraft(columns, row);
    setInlineErrors((prev) => {
      if (Object.keys(rowErrs).length === 0) {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: rowErrs };
    });
  };

  const addInlineRow = () => {
    if (!canAdd) return;
    onChange([...rows, emptySubformRow(field)]);
  };

  const removeRow = (index: number) => {
    if (!canRemove) return;
    const removed = rows[index];
    if (removed) {
      const key = subformRowKey(removed, index);
      setInlineErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
    onChange(rows.filter((_, i) => i !== index));
  };

  const duplicateRow = (index: number) => {
    if (!canAdd) return;
    const copy = { ...rows[index], [EFORM_SUBFORM_ROW_ID_KEY]: newSubformRowId() };
    const next = [...rows];
    next.splice(index + 1, 0, copy);
    onChange(next);
  };

  if (!columns.length) {
    return (
      <p className={`text-sm ${colors.textMuted}`}>
        This sub-form has no columns configured.
      </p>
    );
  }

  if (!useModal) {
    return (
      <div className="space-y-2">
        <div
          className={cn(
            'overflow-x-auto rounded-xl border',
            colors.border,
            colors.surface,
            error && 'border-red-400',
          )}
        >
          <table className="min-w-full text-sm">
            <thead>
              <tr className={cn('border-b bg-slate-50/80 dark:bg-slate-900/50', colors.border)}>
                <th
                  className={cn(
                    'w-10 px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide',
                    colors.textMuted,
                  )}
                >
                  #
                </th>
                {columns.map((col) => (
                  <th
                    key={col.id}
                    className={cn(
                      'px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap',
                      colors.textMuted,
                    )}
                  >
                    {col.label}
                    {col.required ? <span className="text-red-600"> *</span> : null}
                  </th>
                ))}
                {!readOnly ? (
                  <th className="w-24 px-2 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Actions
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (readOnly ? 1 : 2)}
                    className={cn('px-3 py-6 text-center text-sm', colors.textMuted)}
                  >
                    No rows yet
                    {!readOnly ? ' — add a row below' : ''}
                  </td>
                </tr>
              ) : (
                rows.map((row, ri) => {
                  const rowKey = subformRowKey(row, ri);
                  const rowErrs = inlineErrors[rowKey] ?? {};
                  return (
                    <tr key={rowKey} className={cn('border-b last:border-b-0', colors.border)}>
                      <td className={cn('px-2 py-2 tabular-nums align-top', colors.textMuted)}>
                        {ri + 1}
                      </td>
                      {columns.map((col) => (
                        <td key={col.id} className="px-2 py-2 align-top min-w-[7rem]">
                          <NestedFieldControl
                            field={col}
                            value={row[col.key]}
                            readOnly={readOnly}
                            compact
                            error={rowErrs[col.key]}
                            onChange={(v) => updateRow(ri, col.key, v)}
                            onBlur={() => validateInlineRow(ri)}
                          />
                          {rowErrs[col.key] ? (
                            <p className="mt-0.5 text-[11px] text-red-600">{rowErrs[col.key]}</p>
                          ) : null}
                        </td>
                      ))}
                      {!readOnly ? (
                        <td className="px-2 py-2 align-top">
                          <div className="flex justify-end gap-0.5">
                            <button
                              type="button"
                              className={cn(
                                'p-1.5 rounded-lg transition-colors',
                                colors.textMuted,
                                'hover:bg-slate-100 dark:hover:bg-slate-800',
                                !canAdd && 'opacity-30 pointer-events-none',
                              )}
                              title="Duplicate row"
                              aria-label="Duplicate row"
                              onClick={() => duplicateRow(ri)}
                            >
                              <CopyIcon className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              className={cn(
                                'p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40',
                                !canRemove && 'opacity-30 pointer-events-none',
                              )}
                              title="Remove row"
                              aria-label="Remove row"
                              onClick={() => removeRow(ri)}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!readOnly ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canAdd}
              onClick={addInlineRow}
              className="inline-flex items-center gap-1.5"
            >
              <PlusIcon className="w-4 h-4" />
              Add row
            </Button>
            <RowCountHint rows={rows} field={field} colors={colors} />
          </div>
        ) : null}

        {error ? <p className="text-xs text-red-600">{error}</p> : null}
      </div>
    );
  }

  const editorOpen = editor != null;
  const editorReadOnly = readOnly || editor?.mode === 'view';
  const editorTitle =
    editor?.mode === 'add'
      ? `Add ${field.label || 'row'}`
      : editor?.mode === 'edit'
        ? `Edit row ${(editor.index ?? 0) + 1}`
        : `Row ${(editor?.index ?? 0) + 1}`;

  const summaryCols = columns.slice(0, 4);
  const extraColCount = Math.max(0, columns.length - summaryCols.length);

  return (
    <div className="space-y-2">
      <div
        className={cn(
          'overflow-x-auto rounded-xl border',
          colors.border,
          colors.surface,
          error && 'border-red-400',
        )}
      >
        <table className="min-w-full text-sm">
          <thead>
            <tr className={cn('border-b bg-slate-50/80 dark:bg-slate-900/50', colors.border)}>
              <th
                className={cn(
                  'w-10 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide',
                  colors.textMuted,
                )}
              >
                #
              </th>
              {summaryCols.map((col) => (
                <th
                  key={col.id}
                  className={cn(
                    'px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap',
                    colors.textMuted,
                  )}
                >
                  {col.label}
                </th>
              ))}
              {extraColCount > 0 ? (
                <th
                  className={cn(
                    'px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide',
                    colors.textMuted,
                  )}
                >
                  +{extraColCount} more
                </th>
              ) : null}
              <th className="w-28 px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={summaryCols.length + (extraColCount > 0 ? 1 : 0) + 2}
                  className={cn('px-3 py-8 text-center text-sm', colors.textMuted)}
                >
                  No rows yet
                  {!readOnly ? ' — use Add row to open the form' : ''}
                </td>
              </tr>
            ) : (
              rows.map((row, ri) => (
                <tr
                  key={subformRowKey(row, ri)}
                  className={cn(
                    'border-b last:border-b-0 transition-colors',
                    colors.border,
                    'hover:bg-slate-50/70 dark:hover:bg-slate-900/40',
                  )}
                >
                  <td className={cn('px-3 py-2.5 tabular-nums', colors.textMuted)}>{ri + 1}</td>
                  {summaryCols.map((col) => {
                    const display = formatFieldDisplayValue(col, row[col.key]);
                    return (
                      <td key={col.id} className={cn('px-3 py-2.5 max-w-[14rem]', colors.text)}>
                        <button
                          type="button"
                          className="block w-full truncate text-left hover:underline"
                          title={display}
                          onClick={() => (readOnly ? openView(ri) : openEdit(ri))}
                        >
                          {truncateDisplay(display)}
                        </button>
                      </td>
                    );
                  })}
                  {extraColCount > 0 ? (
                    <td className={cn('px-3 py-2.5 text-xs', colors.textMuted)}>
                      <button
                        type="button"
                        className="hover:underline"
                        onClick={() => (readOnly ? openView(ri) : openEdit(ri))}
                      >
                        View all
                      </button>
                    </td>
                  ) : null}
                  <td className="px-3 py-2.5">
                    <div className="flex justify-end gap-0.5">
                      <button
                        type="button"
                        className={cn(
                          'p-1.5 rounded-lg transition-colors',
                          colors.textMuted,
                          'hover:bg-slate-100 dark:hover:bg-slate-800',
                        )}
                        title={readOnly ? 'View row' : 'Edit row'}
                        aria-label={readOnly ? 'View row' : 'Edit row'}
                        onClick={() => (readOnly ? openView(ri) : openEdit(ri))}
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      {!readOnly ? (
                        <>
                          <button
                            type="button"
                            className={cn(
                              'p-1.5 rounded-lg transition-colors',
                              colors.textMuted,
                              'hover:bg-slate-100 dark:hover:bg-slate-800',
                              !canAdd && 'opacity-30 pointer-events-none',
                            )}
                            title="Duplicate row"
                            aria-label="Duplicate row"
                            onClick={() => duplicateRow(ri)}
                          >
                            <CopyIcon className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            className={cn(
                              'p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40',
                              !canRemove && 'opacity-30 pointer-events-none',
                            )}
                            title="Remove row"
                            aria-label="Remove row"
                            onClick={() => removeRow(ri)}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!readOnly ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canAdd}
            onClick={openAdd}
            className="inline-flex items-center gap-1.5"
          >
            <PlusIcon className="w-4 h-4" />
            Add row
          </Button>
          <RowCountHint rows={rows} field={field} colors={colors} />
        </div>
      ) : null}

      {error ? <p className="text-xs text-red-600">{error}</p> : null}

      <Modal isOpen={editorOpen} onClose={closeEditor} size="lg">
        <ModalHeader>
          <ModalTitle>{editorTitle}</ModalTitle>
        </ModalHeader>
        <ModalContent className="space-y-4">
          {columns.map((col) => {
            const controlId = `subform-${field.key}-${col.key}`;
            const isBool = col.type === 'checkbox' || col.type === 'switch';
            return (
              <div key={col.id}>
                {!isBool ? (
                  <Label htmlFor={controlId}>
                    {col.label}
                    {col.required ? <span className="text-red-600"> *</span> : null}
                  </Label>
                ) : null}
                <div className={isBool ? '' : 'mt-1.5'}>
                  <NestedFieldControl
                    id={controlId}
                    field={col}
                    value={draft[col.key]}
                    readOnly={editorReadOnly}
                    error={draftErrors[col.key]}
                    onChange={(v) => {
                      setDraft((prev) => ({ ...prev, [col.key]: v }));
                      setDraftErrors((prev) => {
                        if (!prev[col.key]) return prev;
                        const next = { ...prev };
                        delete next[col.key];
                        return next;
                      });
                    }}
                  />
                </div>
                {draftErrors[col.key] && (col.type === 'checkbox' || col.type === 'switch') ? (
                  <p className="mt-1 text-xs text-red-600">{draftErrors[col.key]}</p>
                ) : null}
              </div>
            );
          })}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={closeEditor}>
            {editorReadOnly ? 'Close' : 'Cancel'}
          </Button>
          {!editorReadOnly ? (
            <Button type="button" onClick={saveDraft}>
              {editor?.mode === 'add' ? 'Add row' : 'Save'}
            </Button>
          ) : null}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default EFormSubformField;
